import React, { useState, ReactElement, useRef, useEffect } from 'react'
import {
  Menu,
  Button,
  Box,
  Typography,
  Divider,
  PopoverOrigin,
} from '@mui/material'
import { styled } from '@mui/system'
import { CustomMenuList } from './CustomMenuListComponent'
import { AnimatedChevronIcon } from './Svgs'

interface CustomButtonProps {
  isOpen: boolean
  notCentered: boolean
}

const CustomButton = styled(
  React.forwardRef<
    HTMLButtonElement,
    CustomButtonProps & React.ComponentProps<typeof Button>
  >(({ isOpen, notCentered, ...rest }, ref) => <Button {...rest} ref={ref} />),
)<CustomButtonProps>(({ isOpen, notCentered }) => ({
  border: 'none',
  width: '100%',
  padding: '10px',
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'initial',
  opacity: 1,
  transition: 'opacity 0.3s ease, background-color 0s',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent',
    opacity: 1,
  },
  '&:active, &:focus': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  '& .MuiTouchRipple-root': {
    display: 'none',
  },
}))

interface CustomDropdownProps {
  children: ReactElement | ReactElement[]
  color: string
  height?: number
  width?: number
  menuMargin?: string
  withoutOpacity?: boolean
  title?: string
  imageSrcs?: string[]
  svg?: JSX.Element
  disabled?: boolean
  buttonStyleProps?: React.CSSProperties
  titleStyleProps?: React.CSSProperties
  headerComponent?: JSX.Element
  hideArrow?: boolean
  anchorOrigin?: PopoverOrigin
  transformOrigin?: PopoverOrigin
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  children,
  color,
  height,
  width,
  menuMargin,
  title,
  withoutOpacity,
  imageSrcs,
  svg,
  disabled,
  buttonStyleProps,
  titleStyleProps,
  headerComponent,
  hideArrow,
  anchorOrigin,
  transformOrigin,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [buttonWidth, setButtonWidth] = useState<number>(0)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const anchorOriginValue: PopoverOrigin = anchorOrigin
    ? anchorOrigin
    : {
        vertical: 'bottom',
        horizontal: 'left',
      }
  const transformOriginValue: PopoverOrigin = transformOrigin
    ? transformOrigin
    : {
        vertical: 'top',
        horizontal: 'left',
      }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const isOpen = Boolean(anchorEl)

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.getBoundingClientRect().width)
    }
  }, [buttonRef, isOpen])

  return (
    <>
      <CustomButton
        disabled={disabled}
        onClick={handleClick}
        ref={buttonRef}
        isOpen={isOpen}
        notCentered={withoutOpacity!}
        style={{
          ...buttonStyleProps,
        }}
      >
        {headerComponent ? (
          <>{headerComponent}</>
        ) : imageSrcs ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {imageSrcs.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`dropdown ${index}`}
                style={{ display: 'block', marginRight: '2px' }}
              />
            ))}
            {title ? (
              <Typography sx={{ color: 'black', textTransform: 'capitalize' }}>
                {title}
              </Typography>
            ) : (
              ''
            )}
          </Box>
        ) : svg ? (
          svg
        ) : (
          <Typography
            variant='button'
            style={{
              maxWidth: '90%',
              lineHeight: 'normal',
              fontSize: '12px',
              textTransform: 'capitalize',
              color: `${disabled ? '#BBBCBC' : color}`,
              ...titleStyleProps,
            }}
          >
            {title?.toLocaleLowerCase()}
          </Typography>
        )}
        {!hideArrow && (
          <AnimatedChevronIcon
            open={isOpen}
            sx={{
              ml: '6px',
              color: disabled ? '#BBBCBC' : color,
              fontSize: '12px',
            }}
          />
        )}
      </CustomButton>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'custom-button',
          disablePadding: true,
          component: CustomMenuList,
        }}
        slotProps={{
          paper: {
            sx: {
              height: `${height}px`,
              width: `${width ? width : buttonWidth}px`,
              margin: menuMargin ? menuMargin : '4px 0 0 0',
              border: 'none',
              padding: 0,
            },
          },
        }}
        anchorOrigin={anchorOriginValue}
        transformOrigin={transformOriginValue}
      >
        {React.Children.map(children, (child, index) => {
          const elements = []
          if (index > 0) {
            elements.push(
              <Divider
                key={`divider-${index}`}
                flexItem
                sx={{ my: '0 !important' }}
              />,
            )
          }
          elements.push(
            React.cloneElement(child, {
              key: index,
              onClick: (event: React.MouseEventHandler<HTMLLIElement>) => {
                if (child.props.onClick) {
                  child.props.onClick(event)
                }
                handleClose()
              },
            }),
          )
          return elements
        })}
      </Menu>
    </>
  )
}

export default CustomDropdown
